// src/components/onboarding/JoyrideProvider.tsx
import React, { useEffect, useState } from 'react';
import Joyride, { CallBackProps, Step } from 'react-joyride';
import { useSetState } from 'react-use';
import { useAuth } from 'src/libs/rainbow';
import { useConnectModal } from '@rainbow-me/rainbowkit';
import { useHistory, useParams } from 'react-router';
import useWindowSize from 'src/hooks/useWindowSize';
import CustomTooltip from './customTooltip';
import { useAppTranslation } from 'src/i18n/hooks';

interface Props {
  children?: React.ReactNode;
  setIsOpenMenuMobile: (bool: boolean) => void;
  isOpenMenuMobile?: boolean;
}

const storageKey = 'onboardingStep';

interface State {
  run: boolean;
  steps: Step[];
}

const JoyrideProvider: React.FC<Props> = ({ children, setIsOpenMenuMobile, isOpenMenuMobile }) => {
  const { t } = useAppTranslation('joiRide');
  const INTRO_STEPS: Step[] = [
    {
      target: '#connect-button',
      title: t('connectYourWallet'),
      content: t('chooseWalletProvider'),
      disableBeacon: true,
    },
    {
      target: '#connect-button',
      content: '',
    },
  ];

  const MAIN_STEPS: Step[] = [
    {
      target: '#deposit-button',
      content: t('depositFundsContent'),
      title: t('depositFundsTitle'),
      disableBeacon: true,
    },
    {
      target: '#trading-pair',
      content: t('selectTradingPairContent'),
      title: t('selectTradingPairTitle'),
      disableBeacon: true,
    },
    {
      target: '#place-order-button',
      content: t('startTradingContent'),
      title: t('startTradingTitle'),
      disableBeacon: true,
    },
    {
      target: '#withdraw-button',
      content: t('withdrawFundsContent'),
      title: t('withdrawFundsTitle'),
      disableBeacon: true,
    },
    {
      target: 'final-step',
      content: '',
    },
  ];

  const FINAL_STEPS: Step[] = [
    {
      target: '#deposit-button-liquidity-pool',
      content: t('provideLiquidityContent'),
      title: t('provideLiquidityTitle'),
      disableBeacon: true,
    },
    {
      target: '#more-menu',
      content: t('needHelpContent'),
      title: t('needHelpTitle'),
      disableBeacon: true,
    },
  ];

  const { isAuthenticated } = useAuth();
  const { openConnectModal } = useConnectModal();
  const { isDesktop } = useWindowSize();
  const history = useHistory();
  const params = useParams();

  const [currentStep, setCurrentStep] = useState(localStorage.getItem(storageKey) || '0');

  const [introState, setIntroState] = useSetState<State>({
    run: false,
    steps: INTRO_STEPS,
  });

  const [mainState, setMainState] = useSetState<State>({
    run: false,
    steps: MAIN_STEPS,
  });

  const [finalState, setFinalState] = useSetState<State>({
    run: false,
    steps: FINAL_STEPS,
  });

  const isStep = (step: string | null, values: string[]) => step !== null && values.includes(step);

  const handleIntroCallback = ({ action, index, type }: CallBackProps) => {
    if (type === 'step:after' && index === 0 && action === 'next') {
      if (openConnectModal) {
        openConnectModal();
        localStorage.setItem(storageKey, '0');
        setIntroState({ run: false });
      }
    }
    if (action === 'skip') {
      localStorage.setItem(storageKey, '6');
    }
  };

  const handleMainCallback = async ({ action, index, type }: CallBackProps) => {
    if (action === 'next' && type === 'step:after') {
      const nextStep = (index + 1).toString();
      localStorage.setItem(storageKey, nextStep);
      setCurrentStep(nextStep);
    }
    if (index >= 1 && index < 3 && !window.location.href.includes('/trade')) {
      history.push('/trade');
    }
    if (type === 'step:after' && index === 3 && action === 'next') {
      localStorage.setItem(storageKey, '4');
      history.push('/vmm');
      setMainState({ run: false });
    }
    if (type === 'step:after' && index === 0 && action === 'next' && isOpenMenuMobile) {
      setIsOpenMenuMobile(false);
    }

    if (
      ((type === 'step:before' || type === 'tour:start') && [2, 3].includes(index)) ||
      (type === 'step:after' && index === 1 && action === 'next')
    ) {
      while (!(window as any).changeTab) {
        await new Promise((resolve) => setTimeout(resolve, 500));
      }
      (window as any).changeTab('trade');
      await new Promise((resolve) => setTimeout(resolve, 500));
      setMainState({ run: true });
    }
    if (action === 'skip') {
      localStorage.setItem(storageKey, '6');
    }
  };

  const handleFinalCallback = ({ action, index, type }: CallBackProps) => {
    if (action === 'next' && type === 'step:after') {
      setCurrentStep('6');
      localStorage.setItem(storageKey, '6');
    }
    if (
      !isDesktop &&
      !isOpenMenuMobile &&
      index === 0 &&
      type === 'step:after' &&
      action === 'next'
    ) {
      setIsOpenMenuMobile(true);
    }
    if (index === 1 && type === 'step:after' && action === 'next') {
      localStorage.setItem(storageKey, '6');
      setFinalState({ run: false });
    }
    if (action === 'skip') {
      localStorage.setItem(storageKey, '6');
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      if (
        !isDesktop &&
        !isOpenMenuMobile &&
        isStep(localStorage.getItem(storageKey) ?? '0', ['0', '1'])
      ) {
        setIsOpenMenuMobile(true);
      } else if (localStorage.getItem(storageKey)) {
        setMainState({ run: true });
      } else if (!localStorage.getItem(storageKey)) {
        setMainState({ run: true });
        setIntroState({ run: false });
        localStorage.setItem(storageKey, '0');
      }
    }
  }, [isAuthenticated, history.location.pathname]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const step = localStorage.getItem(storageKey);
      if ((!step || step === '0') && !isAuthenticated) {
        if (!isDesktop && !isOpenMenuMobile) {
          setIsOpenMenuMobile(true);
        } else setIntroState({ run: true });
      }
    }, 3000);
    return () => clearTimeout(timer);
  }, [setIntroState, isAuthenticated]);

  useEffect(() => {
    const step = localStorage.getItem(storageKey);
    if (isOpenMenuMobile) {
      if ((!step || step === '0') && !isAuthenticated) {
        setIntroState({ run: true });
      } else {
        setMainState({ run: true });
      }
    }
  }, [isOpenMenuMobile]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (history.location.pathname === '/vmm' && localStorage.getItem(storageKey) === '4') {
      timer = setTimeout(() => {
        setFinalState({ run: true });
      }, 2000);
    }
    if (localStorage.getItem(storageKey) === '5') {
      if (!isDesktop && !isOpenMenuMobile) {
        setIsOpenMenuMobile(true);
      }
      timer = setTimeout(() => {
        setFinalState({ run: true });
      }, 2000);
    }
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [history.location.pathname, setFinalState]);

  return (
    <>
      <Joyride
        callback={handleFinalCallback}
        continuous
        run={finalState.run}
        steps={finalState.steps}
        tooltipComponent={(props) => <CustomTooltip {...props} type="final" />}
        scrollOffset={64}
        scrollToFirstStep
        showSkipButton
        hideBackButton
        disableOverlay
        disableOverlayClose={true}
        stepIndex={currentStep === '6' ? 1 : 0}
        locale={{ back: 'Back', close: 'Close', last: 'Finish', next: 'Next', skip: 'Skip' }}
        styles={{ options: { zIndex: 10000 } }}
      />
      <Joyride
        callback={handleIntroCallback}
        continuous
        run={introState.run}
        steps={introState.steps}
        disableOverlay
        tooltipComponent={(props) => <CustomTooltip {...props} type="intro" />}
        scrollOffset={64}
        scrollToFirstStep
        showSkipButton
        hideBackButton
        disableOverlayClose={true}
        locale={{ back: 'Back', close: 'Close', last: 'Finish', next: 'Next Step', skip: 'Skip' }}
        styles={{ options: { zIndex: 10000 } }}
      />
      <Joyride
        callback={handleMainCallback}
        continuous
        run={mainState.run}
        steps={mainState.steps}
        disableOverlayClose={true}
        tooltipComponent={(props) => <CustomTooltip {...props} type="main" />}
        scrollOffset={64}
        scrollToFirstStep
        showSkipButton
        disableOverlay
        hideBackButton
        stepIndex={parseInt(currentStep || '0', 10)}
        locale={{ back: 'Back', close: 'Close', last: 'Finish', next: 'Next', skip: 'Skip' }}
        styles={{ options: { zIndex: 10000 } }}
      />
      {children}
    </>
  );
};

export default JoyrideProvider;
